/**
 * DefaultAccountsScreen — Set default GL accounts per voucher type.
 * Finance Masters → Default Accounts
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Save, Trash2, RefreshCw, Info } from 'lucide-react';
import { erpToast } from '../../components/erp/ERPToast';
import { API_BASE as API, getToken } from '../../lib/api';
import '../../styles/erp-theme.css';

const hdrs = () => ({ Authorization: `Bearer ${getToken() || ''}`, 'Content-Type': 'application/json' });

const VOUCHER_TYPES = [
  { key: 'cash-receipts',       label: 'Cash Receipt',          desc: 'Default cash/party ledger for cash receipts' },
  { key: 'bank-receipts',       label: 'Bank Receipt',          desc: 'Default bank ledger for bank receipts' },
  { key: 'pdc-receipts',        label: 'PDC Receipt',           desc: 'Default PDC ledger for PDC receipts' },
  { key: 'cash-payments',       label: 'Cash Payment',          desc: 'Default cash/party ledger for cash payments' },
  { key: 'bank-payments',       label: 'Bank Payment',          desc: 'Default bank ledger for bank payments' },
  { key: 'pdc-payments',        label: 'PDC Payment',           desc: 'Default PDC ledger for PDC payments' },
  { key: 'petty-cash-receipts', label: 'Petty Cash Receipt',    desc: 'Default petty cash fund ledger' },
  { key: 'petty-cash-payments', label: 'Petty Cash Payment',    desc: 'Default petty cash fund ledger' },
  { key: 'journal-entries',     label: 'Journal Entry',         desc: 'Default suspense/clearing ledger for JV' },
  { key: 'contra-entries',      label: 'Contra Entry',          desc: 'Default contra fund ledger' },
  { key: 'debit-notes',         label: 'Debit Note',            desc: 'Default debit party ledger' },
  { key: 'credit-notes',        label: 'Credit Note',           desc: 'Default credit party ledger' },
];

const DefaultAccountsScreen = ({ theme = 'light' }) => {
  const [defaults, setDefaults] = useState({}); // { voucherType: { GLId, GLName } }
  const [allGL, setAllGL] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [rd, rg] = await Promise.all([
        fetch(`${API}/api/finance/default-accounts`, { headers: hdrs() }),
        fetch(`${API}/api/finance/dropdown/account-list`, { headers: hdrs() }),
      ]);
      const jd = await rd.json();
      const jg = await rg.json();
      if (jd.success) {
        const map = {};
        (jd.data || []).forEach(r => { map[r.VoucherType] = { GLId: r.GLId, GLName: r.GLName }; });
        setDefaults(map);
      }
      if (jg.success) setAllGL(jg.data || []);
    } catch { erpToast.error('Failed to load data'); }
    setLoading(false);
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const handleChange = (voucherType, glId) => {
    const gl = allGL.find(g => String(g.GLId) === String(glId));
    setDefaults(prev => ({ ...prev, [voucherType]: glId ? { GLId: glId, GLName: gl?.GLName || '' } : undefined }));
  };

  const handleSave = async (voucherType) => {
    const entry = defaults[voucherType];
    if (!entry?.GLId) {
      // Clear
      try {
        setSaving(voucherType);
        await fetch(`${API}/api/finance/default-accounts/${encodeURIComponent(voucherType)}`, { method: 'DELETE', headers: hdrs() });
        erpToast.success('Default cleared');
      } catch { erpToast.error('Save failed'); }
      setSaving(null);
      return;
    }
    try {
      setSaving(voucherType);
      const r = await fetch(`${API}/api/finance/default-accounts`, {
        method: 'POST', headers: hdrs(),
        body: JSON.stringify({ voucherType, glId: entry.GLId, glName: entry.GLName }),
      });
      const j = await r.json();
      if (j.success) erpToast.success(`Default saved for ${VOUCHER_TYPES.find(v => v.key === voucherType)?.label}`);
      else erpToast.error(j.error || 'Save failed');
    } catch { erpToast.error('Save failed'); }
    setSaving(null);
  };

  const handleClear = async (voucherType) => {
    setDefaults(prev => { const n = { ...prev }; delete n[voucherType]; return n; });
    try {
      await fetch(`${API}/api/finance/default-accounts/${encodeURIComponent(voucherType)}`, { method: 'DELETE', headers: hdrs() });
      erpToast.success('Default cleared');
    } catch { erpToast.error('Clear failed'); }
  };

  return (
    <div className={`erp-screen${theme === 'dark' ? ' dark' : ''}`} style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <div className="erp-screen-header" style={{ padding: '12px 20px', borderBottom: '1px solid var(--erp-border)', display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0 }}>
        <div style={{ flex: 1 }}>
          <h2 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: 'var(--erp-text)' }}>Default Accounts</h2>
          <p style={{ margin: 0, fontSize: 11, color: 'var(--erp-text-muted)' }}>Set default GL ledger for each voucher type — auto-loads when opening a voucher</p>
        </div>
        <button className="erp-btn erp-btn-secondary" onClick={loadData} disabled={loading} style={{ height: 30, fontSize: 11, display: 'flex', gap: 4, alignItems: 'center' }} data-testid="da-refresh-btn">
          <RefreshCw size={12} className={loading ? 'spin' : ''} /> Refresh
        </button>
      </div>

      {/* Info banner */}
      <div style={{ background: 'var(--erp-info-bg, #e8f4fd)', borderBottom: '1px solid var(--erp-border)', padding: '8px 20px', display: 'flex', gap: 8, alignItems: 'center', fontSize: 11, color: 'var(--erp-text-muted)', flexShrink: 0 }}>
        <Info size={12} />
        When a voucher is opened, if no account is pre-selected, the default account set here is auto-loaded into the header.
      </div>

      {/* Table */}
      <div style={{ flex: 1, overflow: 'auto', padding: 20 }}>
        <table className="erp-table" style={{ width: '100%', fontSize: 12 }}>
          <thead>
            <tr>
              <th style={{ width: 200 }}>Voucher Type</th>
              <th>Description</th>
              <th style={{ width: 300 }}>Default GL Account</th>
              <th style={{ width: 80 }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {VOUCHER_TYPES.map(vt => {
              const current = defaults[vt.key];
              return (
                <tr key={vt.key} data-testid={`da-row-${vt.key}`}>
                  <td style={{ fontWeight: 600 }}>{vt.label}</td>
                  <td style={{ color: 'var(--erp-text-muted)', fontSize: 11 }}>{vt.desc}</td>
                  <td>
                    <select
                      value={current?.GLId || ''}
                      onChange={e => handleChange(vt.key, e.target.value)}
                      className="erp-input erp-select"
                      style={{ width: '100%', fontSize: 11 }}
                      data-testid={`da-select-${vt.key}`}
                    >
                      <option value="">-- No Default --</option>
                      {allGL.map(g => (
                        <option key={g.GLId} value={g.GLId}>{g.GLName}{g.GLType ? ` [${g.GLType}]` : ''}</option>
                      ))}
                    </select>
                  </td>
                  <td style={{ display: 'flex', gap: 4 }}>
                    <button
                      className="erp-btn erp-btn-primary"
                      onClick={() => handleSave(vt.key)}
                      disabled={saving === vt.key}
                      style={{ height: 26, width: 26, padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                      title="Save"
                      data-testid={`da-save-${vt.key}`}
                    >
                      <Save size={11} />
                    </button>
                    {current?.GLId && (
                      <button
                        className="erp-btn erp-btn-danger"
                        onClick={() => handleClear(vt.key)}
                        style={{ height: 26, width: 26, padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                        title="Clear"
                        data-testid={`da-clear-${vt.key}`}
                      >
                        <Trash2 size={11} />
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default DefaultAccountsScreen;
